import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "chat with Alpha about your portfolio" walkthrough. Same code-driven
// approach as PortfolioDemo/ScreenerDemo (no video): types a question, shows
// Alpha researching (typing dots + live-data chips), then streams the answer.

const USER_Q = 'What do you think of my portfolio?';
const CHIPS = ['Your 5 holdings', 'NVDA estimate revisions', 'Institutional 13F flow', 'Market regime'];
const ANSWER =
  'Strong core. NVDA and TSM are your standouts — forward estimates are still ' +
  "being revised up and institutions are accumulating both. COIN is the soft " +
  "spot: weak quality, and it's adding most of your downside risk. Net read: " +
  'Favorable overall, but COIN is worth a closer look. Want me to dig into it?';

export default function AlphaChatDemo({ autoPlay = true, loop = true }) {
  const [phase, setPhase] = useState(0); // 0=typing Q, 1=researching, 2=answering
  const [typedQ, setTypedQ] = useState('');
  const [visibleChips, setVisibleChips] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState('');
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase(0); setTypedQ(''); setVisibleChips(0); setTypedAnswer('');

    // Phase 0 — type the question into the input bar.
    let qi = 0;
    const qInt = setInterval(() => {
      qi++;
      setTypedQ(USER_Q.slice(0, qi));
      if (qi >= USER_Q.length) clearInterval(qInt);
    }, 48);
    timers.current.push(qInt);

    // Phase 1 — "send", Alpha starts researching (typing dots + chips appear).
    timers.current.push(setTimeout(() => setPhase(1), 1900));
    CHIPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisibleChips(i + 1), 2300 + i * 450));
    });

    // Phase 2 — stream Alpha's answer; chips stay pinned below it.
    const answerStart = 2300 + CHIPS.length * 450 + 500;
    timers.current.push(setTimeout(() => {
      setPhase(2);
      let ai = 0;
      const aInt = setInterval(() => {
        ai++;
        setTypedAnswer(ANSWER.slice(0, ai));
        if (ai >= ANSWER.length) clearInterval(aInt);
      }, 20);
      timers.current.push(aInt);
    }, answerStart));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 13000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  const researching = phase === 1;
  const answering = phase === 2;

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/alpha</span>
      </div>
      <div className="demo-body">
        <div className="demo-chat">
          {phase >= 1 && (
            <div className="demo-chat-msg user">
              <div className="demo-chat-bubble">{USER_Q}</div>
            </div>
          )}
          {phase >= 1 && (
            <div className="demo-chat-msg alpha">
              <div className="demo-chat-bubble">
                <div className="demo-chat-name">Alpha</div>
                {researching && (
                  <div className="demo-typing-dots"><span /><span /><span /></div>
                )}
                {answering && (
                  <>
                    {typedAnswer}
                    {typedAnswer.length < ANSWER.length && <span className="demo-cursor" />}
                  </>
                )}
                {visibleChips > 0 && (
                  <div className="demo-chips">
                    {CHIPS.slice(0, visibleChips).map((c) => (
                      <span key={c} className="demo-chip">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Chat input bar with the typing question */}
      <div className="demo-input-row" style={{ margin: '0 1.25rem 1.25rem' }}>
        <span className="demo-input-label">💬</span>
        <span className="demo-input-text">
          {phase === 0 ? typedQ : ''}
          {phase === 0 && <span className="demo-cursor" />}
        </span>
        <span className={`demo-btn ${phase >= 1 ? 'clicked' : ''}`}>Send</span>
      </div>
    </div>
  );
}
